/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";

import { useDisclosure } from "@chakra-ui/react";

import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import {
  IDepartment,
  TeamMember
} from "@/jenesys-types/jenesys-entities/config";

import { useCompany } from "@/hooks/util/useCompany";
import {
  addDepartment,
  deleteMember,
  inviteMember,
  loadDepartments,
  loadTeamMembers,
  resendEmail,
  selectTeamSettings,
  updateMember
} from "@/store/features/clientSettings/team/team";
import { selectCompany } from "@/store/features/companies/companies";

const roles = [
  { name: "PRACTICE ADMIN", id: "PRACTICE_ADMIN" },
  { name: "PRACTICE FINANCE", id: "PRACTICE_FINANCE" }
];

export const useCompanyTeam = () => {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const { selectedCompany, activeCompanyId } = useCompany();
  const dispatch = useDispatch();

  const addDepartmentDisclosure = useDisclosure();
  const deleteMemberDisclosure = useDisclosure();
  const viewMemberDisclosure = useDisclosure();

  useEffect(() => {
    if (activeCompanyId) {
      dispatch(
        loadTeamMembers(activeCompanyId as string, {
          searchQuery,
          page
        })
      );
    }
  }, [dispatch, activeCompanyId, searchQuery, page]);

  useEffect(() => {
    if (activeCompanyId) {
      dispatch(loadDepartments(activeCompanyId as string));
    }
  }, [dispatch, activeCompanyId]);

  const company = useSelector(selectCompany);

  const {
    teamMembers,
    isTeamMembersLoading,
    departments,
    isDepartmentsLoading: isFetchingDepartments,
    isMemberAdding: isAddingMember,
    isMemberUpdating: isSavingMember,
    isMemberDeleting: isRemovingMember,
    isDepartmentAdding: isAddingDepartment
  } = useSelector(selectTeamSettings);

  const debouncedOnChange = useCallback(
    debounce((value: string) => {
      setSearchQuery(value);
    }, 500),
    []
  );

  const handleDeleteMember = () => {
    dispatch(
      deleteMember(selectedMember?.id!!, activeCompanyId!!, () => {
        setSelectedMember(null);
        deleteMemberDisclosure.onClose();
      })
    );
  };
  const handleInviteResend = () => {
    if (selectedMember?.id) {
      dispatch(resendEmail(activeCompanyId, selectedMember.id));
    }
  };

  const handleSubmitAddDepartment = async (payload: Partial<IDepartment>) =>
    new Promise<any>((resolve) => {
      dispatch(
        addDepartment(activeCompanyId!!, payload, (response: any) => {
          resolve(response);
          addDepartmentDisclosure.onClose();
        })
      );
    });
  const refreshTeamList = () =>
    dispatch(
      loadTeamMembers(activeCompanyId as string, {
        searchQuery,
        page
      })
    );

  const handleSubmitAddMember = (payload: any) =>
    dispatch(inviteMember(activeCompanyId!!, payload, () => refreshTeamList()));

  const handleSubmitMemberUpdates = (payload: any) =>
    dispatch(
      updateMember(payload.id, activeCompanyId!!, payload, () =>
        refreshTeamList()
      )
    );

  const triggerAddMember = () => {
    viewMemberDisclosure.onOpen();
  };

  const triggerDeleteWarning = (member: TeamMember) => {
    setSelectedMember(member);
    deleteMemberDisclosure.onOpen();
  };

  const openMemberDetails = (member: TeamMember) => {
    setSelectedMember(member);
    viewMemberDisclosure.onOpen();
  };

  const handlePageClick = (event: { selected: number }) => {
    setPage(event.selected);
  };

  const onCloseModal = () => {
    setSelectedMember(null);
    viewMemberDisclosure.onClose();
  };

  const onMemberMenuClick = (member: TeamMember) => {
    setSelectedMember(member);
  };

  return {
    teamMembers,
    page,
    roles,
    company,
    departments,
    searchQuery,
    selectedCompany,
    selectedMember,
    isAddingMember,
    isSavingMember,
    isTeamMembersLoading,
    isRemovingMember,
    isAddingDepartment,
    viewMemberDisclosure,
    isFetchingDepartments,
    deleteMemberDisclosure,
    addDepartmentDisclosure,
    debouncedOnChange,
    onCloseModal,
    handlePageClick,
    triggerAddMember,
    setSelectedMember,
    openMemberDetails,
    handleDeleteMember,
    triggerDeleteWarning,
    onMemberMenuClick,
    handleInviteResend,
    handleSubmitAddMember,
    handleSubmitMemberUpdates,
    handleSubmitAddDepartment
  };
};
